
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCategoryStore } from "@/store/api/categoryStore";

export function CategoryTab() {
  const { t } = useTranslation("settings");
  const [newCategory, setNewCategory] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const { categories, addCategory, updateCategory, deleteCategory, fetchCategories } = useCategoryStore();

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      toast.error(t("category.errors.nameRequired"));
      return;
    }

    const result = await addCategory(newCategory.trim());
    if (result) {
      toast.success(t("category.success.added"));
      setNewCategory("");
    } else {
      toast.error(t("category.errors.addFailed"));
    }
  };

  const handleStartEdit = (id: string, name: string) => {
    setEditingId(id);
    setEditingName(name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    if (!editingName.trim()) {
      toast.error(t("category.errors.nameRequired"));
      return;
    }

    const success = await updateCategory(editingId, editingName.trim());
    if (success) {
      toast.success(t("category.success.updated"));
      setEditingId(null);
      setEditingName("");
    } else {
      toast.error(t("category.errors.updateFailed"));
    }
  };

  const handleDeleteCategory = async (id: string) => {
    const success = await deleteCategory(id);
    if (success) {
      toast.success(t("category.success.deleted"));
    } else {
      toast.error(t("category.errors.deleteFailed"));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">{t("category.title")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("category.description")}
        </p>
      </div>

      <div className="flex gap-4">
        <Input
          placeholder={t("category.placeholders.name")}
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          className="max-w-sm"
        />
        <Button onClick={handleAddCategory}>
          <Plus className="h-4 w-4 mr-2" />
          {t("category.actions.add")}
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("category.table.name")}</TableHead>
            <TableHead className="w-[120px]">{t("category.table.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((category) => (
            <TableRow key={category.id}>
              <TableCell>
                {editingId === category.id ? (
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEdit();
                      if (e.key === "Escape") handleCancelEdit();
                    }}
                    className="max-w-sm"
                    autoFocus
                  />
                ) : (
                  category.name
                )}
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {editingId === category.id ? (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleSaveEdit}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleStartEdit(category.id, category.name)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCategory(category.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
